import type {
  ProposalPayload,
  GetProposalApiResponseV2,
  RankedSpace,
  SpaceRatingApiEntry,
} from "@/features/vision-360/ai-mode/types";
import { reconstructRoomCombinations } from "@/features/vision-360/ai-mode/utils/comboGrouping";
import { getVipOfficePricePerPosition } from "@/features/vision-360/constants/vip-office-price-table";

// HubSpot serializes company `numberofemployees` as a string; anything
// missing or unparseable means "unknown" (null), never 0.
export function parseEmployeeCount(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Wire → UI: current thumbs state per space id. Visit-only entries (reasons
 * captured after scheduling, no thumbs) carry no state for the widget and are
 * skipped — they exist in the document but nothing in the UI renders them. */
export function mapSpaceRatings(
  entries: SpaceRatingApiEntry[] | undefined,
): ProposalPayload["spaceRatings"] {
  if (!entries?.length) return undefined;
  const ratings: NonNullable<ProposalPayload["spaceRatings"]> = {};
  for (const entry of entries) {
    if (entry.rating !== "up" && entry.rating !== "down") continue;
    ratings[entry.space_id] = {
      rating: entry.rating,
      reasons: entry.reasons ?? [],
      subReasons: entry.sub_reasons ?? [],
      comment: entry.comment ?? "",
    };
  }
  return Object.keys(ratings).length > 0 ? ratings : undefined;
}

/**
 * GET /api/v1/proposals/{uuid} → the payload the proposal page and the
 * builder's edit mode read, for the new format only: `selected_spaces` saved
 * as RankedSpace (what the builder sends). The caller decides the format —
 * `getProposal` checks for `spaceName` on the first space and maps older
 * proposals itself; this function trusts the shape and doesn't check it.
 */
export function mapNewFormatProposal(
  apiData: GetProposalApiResponseV2,
  proposalId: string,
): ProposalPayload {
  type NewFormatSpace = RankedSpace & {
    spaceAmenities?: string[];
    detailedScoreAnalysis?: RankedSpace["detailedScoreAnalysis"];
    scoreDetails?: RankedSpace["scoreDetails"];
  };
  type NewFormatAdj = {
    space_id: string;
    extra_value: number;
    margin: number | null;
    client_price: number;
    credits: number;
    extra_credits: number;
    total_credits: number;
    total_price: number;
    setup_cost: number;
    setup_credits?: number;
    market_discount_pct?: number;
  };

  const newSpaces = apiData.selected_spaces as unknown as NewFormatSpace[];
  const newAdjs = (apiData.space_adjustments ??
    []) as unknown as NewFormatAdj[];

  const allRecommendedIds = apiData.recommended_space_ids ?? [];
  const maxRecommended = Math.ceil(newSpaces.length * 0.3);
  const recommendedIds = new Set(allRecommendedIds.slice(0, maxRecommended));

  const suggestedSpaces: RankedSpace[] = newSpaces.map((s) => {
    const isVipOffice = s.origin === "integration" && s.source === "vip-office";
    let monthlyPrice = s.monthlyPrice;
    if (
      isVipOffice &&
      (!monthlyPrice || monthlyPrice === 0) &&
      s.capacity > 0
    ) {
      const pricePerPos = getVipOfficePricePerPosition(
        s.roomName,
        s.spaceName,
        s.address,
        s.city,
      );
      if (pricePerPos > 0) monthlyPrice = pricePerPos * s.capacity;
    }
    return {
      id: s.id,
      searchId: s.searchId ?? null,
      partnerId: s.partnerId,
      spaceName: s.spaceName,
      roomName: s.roomName,
      city: s.city,
      address: s.address,
      lat: s.lat,
      lng: s.lng,
      pricePerWorkstation: s.pricePerWorkstation,
      monthlyPrice,
      credits: s.credits,
      capacity: s.capacity,
      sizeM2: s.sizeM2,
      score: s.score,
      amenities: s.amenities ?? [],
      spaceAmenities: s.spaceAmenities,
      // Compliance flags of the building (PED-12757). Passed through as
      // sent: `undefined` means the generator didn't inform, and the UI
      // renders nothing rather than a negative.
      spaceHasAvcb: s.spaceHasAvcb,
      spaceHasOperatingPermit: s.spaceHasOperatingPermit,
      imageUrl: s.imageUrl,
      images: s.images ?? [],
      spacePhotos: s.spacePhotos ?? [],
      selectedImages: s.selectedImages,
      selectedSpacePhotos: s.selectedSpacePhotos,
      groupIds: s.groupIds,
      isIndividual: s.isIndividual,
      origin: s.origin ?? null,
      source: s.source ?? null,
      isRecommended: s.isRecommended ?? recommendedIds.has(s.id),
      detailedScoreAnalysis: s.detailedScoreAnalysis,
      scoreDetails: s.scoreDetails,
    };
  });

  const spaceAdjustments = newAdjs.map((adj) => ({
    spaceId: adj.space_id,
    extraValue: adj.extra_value,
    margin: adj.margin ?? 0,
    clientPrice: adj.client_price,
    credits: adj.credits,
    extraCredits: adj.extra_credits,
    totalCredits: adj.total_credits,
    totalPrice: adj.total_price,
    setupCost: adj.setup_cost,
    setupCredits: adj.setup_credits,
    marketDiscountPct: adj.market_discount_pct,
  }));

  const rawCreatedAt = apiData.createdAt;
  const createdAtStr =
    rawCreatedAt != null && typeof rawCreatedAt === "object"
      ? (rawCreatedAt as { $date: string }).$date
      : ((rawCreatedAt as string) ?? new Date().toISOString());

  const rawValidity = apiData.proposal_validity;
  let expiresAtTimestamp: number;
  if (typeof rawValidity === "string" && isNaN(Number(rawValidity))) {
    expiresAtTimestamp = new Date(rawValidity).getTime();
  } else if (
    typeof apiData.expiresAt === "string" &&
    isNaN(Number(apiData.expiresAt))
  ) {
    expiresAtTimestamp = new Date(apiData.expiresAt).getTime();
  } else {
    const validityDays = Number(rawValidity ?? apiData.expiresAt ?? 7);
    expiresAtTimestamp =
      new Date(createdAtStr).getTime() + validityDays * 24 * 60 * 60 * 1000;
  }

  const hc = apiData.hard_constraints;

  // Rebuild the selection split from what persists on each space: combos from
  // groupIds, individual picks from isIndividual (older proposals without the
  // flag fall back to selected_office_ids).
  const restoredCombos = reconstructRoomCombinations(suggestedSpaces);
  const restoredIndividualIds = suggestedSpaces.some(
    (s) => s.isIndividual != null,
  )
    ? suggestedSpaces.filter((s) => s.isIndividual).map((s) => s.id)
    : (apiData.selected_office_ids ?? []);

  return {
    uuid: proposalId,
    generatedAt: createdAtStr,
    suggestedSpaces,
    spaceRatings: mapSpaceRatings(apiData.space_ratings),
    proposalType: apiData.proposal_type as ProposalPayload["proposalType"],
    proposalOrigin: apiData.proposal_origin,
    createdBy: apiData.created_by
      ? {
          userId: apiData.created_by.user_id,
          email: apiData.created_by.email,
          fullName: apiData.created_by.full_name,
        }
      : undefined,
    versionNumber: apiData.version_number,
    updatedBy: apiData.updated_by
      ? {
          userId: apiData.updated_by.user_id,
          email: apiData.updated_by.email,
          fullName: apiData.updated_by.full_name,
        }
      : undefined,
    updatedAt: apiData.updated_at ?? undefined,
    versions: apiData.versions?.map((v) => ({
      versionNumber: v.version_number,
      snapshotAt: v.snapshot_at,
      updatedBy: v.updated_by
        ? {
            userId: v.updated_by.user_id,
            email: v.updated_by.email,
            fullName: v.updated_by.full_name,
          }
        : null,
      snapshot: v.snapshot,
      diffs: v.diffs,
    })),
    state: {
      currentStep: 3,
      completedSteps: [0, 1, 2, 3],
      diagnosis: {
        dealId: apiData.deal_context.deal_id ?? "",
        companyName:
          apiData.company_name ?? apiData.deal_context.company_name ?? "",
        contactName: (apiData.deal_context.contact_name as string) ?? "",
        contactEmail: (apiData.deal_context.contact_email as string) ?? "",
        contactPhone: (apiData.deal_context.contact_phone as string) ?? "",
        numberOfEmployees: parseEmployeeCount(
          apiData.deal_context.numberofemployees,
        ),
        segment:
          (apiData.deal_context.company_industry as string) ||
          (apiData.deal_context.segment as string) ||
          "",
        targetCity: hc.city ?? "",
        positionsMin: hc.min_capacity ?? hc.capacity_min ?? "",
        positionsMax: hc.max_capacity ?? hc.capacity_max ?? "",
        monthlyBudget:
          hc.max_budget != null
            ? String(hc.max_budget)
            : hc.budget_max != null
              ? String(hc.budget_max)
              : "",
        needs: apiData.summary_narrative ?? "",
        selectedLocation:
          apiData.location_lat != null && apiData.location_lng != null
            ? {
                label: apiData.location_label ?? hc.city ?? "",
                lat: Number(apiData.location_lat),
                lng: Number(apiData.location_lng),
              }
            : null,
      },
      location:
        apiData.location_lat != null && apiData.location_lng != null
          ? {
              location: {
                label: apiData.location_label ?? hc.city ?? "",
                lat: Number(apiData.location_lat),
                lng: Number(apiData.location_lng),
              },
              radiusKm: apiData.radius_km ?? null,
              selectedSpaceIds: restoredIndividualIds,
              selectedComboIds: restoredCombos.map((c) => c.combinationId),
              rankedSpaces: suggestedSpaces,
              roomCombinations: restoredCombos,
              recommendationHash: apiData.recommendationHash ?? null,
            }
          : null,
      adjustments: {
        creditsPrice: apiData.credits_price ?? 0,
        contractMonths: (apiData.contract_months ?? null) as
          | 6
          | 12
          | 24
          | 36
          | null,
        spaceAdjustments,
        proposalValidity: expiresAtTimestamp,
        sellerNotes: apiData.seller_notes ?? "",
        recommendedSpaceIds: apiData.recommended_space_ids ?? [],
        selectedSpaces: suggestedSpaces,
        includeFlexCredits: apiData.include_flex_credits ?? false,
        flexCredits: apiData.flex_credits,
        showLicenseFee: apiData.show_license_fee ?? false,
        licenseFeeMode: apiData.license_fee_mode,
        licenseFeePercentage: apiData.license_fee_percentage,
        licenseFeeFixed: apiData.license_fee_fixed,
        licenseFeeValue: apiData.license_fee_value,
        licenseFeePeriod: apiData.license_fee_period,
        includeSetupFee: apiData.include_setup_fee ?? false,
        setupFee: apiData.setup_fee,
      },
    },
  };
}
